/**
 * Mutation hook for 4 保険者別明細 PDF buttons on 統計一覧表.
 * Opens PDF in a new tab — mirrors RA2 window.open + form.target pattern.
 */

import { useMutation } from '@tanstack/react-query';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import useAppToast from '@/hooks/useAppToast';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { createStatisticalOtherPdfPreviewUrl } from '@/services/main/statisticalReport';
import { getApiErrorMessage } from '@/utils/apiError';

function resolveServiceType(theme: { serviceType?: string } | undefined): string {
  if (theme?.serviceType === SERVICE_TYPE_A2 || theme?.serviceType === SERVICE_TYPE_R2) {
    return theme.serviceType;
  }
  return SERVICE_TYPE_A2;
}

export function useStatisticalPrintOtherMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const theme = useTheme();
  const toast = useAppToast();

  const { mutateAsync, isPending: isLoading } = useMutation<
    string,
    Error,
    { yearmonth: string; massagerId: number | null; pdfPattern: '1' | '2' | '3' | '4' }
  >({
    mutationFn: ({ yearmonth, massagerId, pdfPattern }) =>
      createStatisticalOtherPdfPreviewUrl(
        tenant,
        clinicNumber,
        yearmonth,
        massagerId,
        pdfPattern,
        resolveServiceType(theme),
      ),
    onError: error => {
      toast.error(getApiErrorMessage(error, '印刷に失敗しました。'));
    },
  });

  const triggerPrint = async (
    yearmonth: string,
    massagerId: number | null,
    pdfPattern: '1' | '2' | '3' | '4',
  ) => {
    const tab = window.open('', '_blank');
    try {
      const url = await mutateAsync({ yearmonth, massagerId, pdfPattern });
      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch {
      tab?.close();
    }
  };

  return { triggerPrint, isLoading };
}
